import { useRef, useState, useEffect } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import LoadingButton from "@shared/components/LoadingButton";
import { structuralGroupService } from "../services/structuralGroups";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import Select from "react-select";
import { signeesService } from "@modules/organizationalStructure/signees/services/signees";
import { useDebounce } from "@shared/hooks/useDebounce";
import { notifySuccess, notifyError } from "@config/tostifyConfig/tostifyConfig";
import CustomDatePicker from "@shared/components/CustomDatePicker";

export default function OccupyingPossition({ onClose, editData, onSuccess }) {
    const modalRef = useRef(null);
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [filteredData, setFilteredData] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [saving, setSaving] = useState(false);
    const [showAddRow, setShowAddRow] = useState(false);
    const [titularPersons, setTitularPersons] = useState([]);
    const [occupantPersons, setOccupantPersons] = useState([]);
    const [selectedTitular, setSelectedTitular] = useState(null);
    const [selectedOccupant, setSelectedOccupant] = useState(null);
    const [isLoadingTitular, setIsLoadingTitular] = useState(false);
    const [isLoadingOccupant, setIsLoadingOccupant] = useState(false);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [documentNumber, setDocumentNumber] = useState('');

    const validationSchema = yup.object().shape({
        fromDate: yup.date().required(t('common.required')),
        titular: yup.string().required(t('common.required')),
        occupant: yup.string().required(t('common.required')),
    });
    const { register, handleSubmit, setValue, watch, reset, formState: { errors } } = useForm({
        resolver: yupResolver(validationSchema),
        mode: 'onChange',
    });

    // Custom styles for react-select to make it small
    const customSelectStyles = (hasError) => ({
        control: (base, state) => ({
            ...base,
            minHeight: '24px',
            height: '24px',
            fontSize: '14px',
            padding: '0',
            borderColor: hasError ? 'red' : (state.isFocused ? '#2684ff' : base.borderColor),
            borderWidth: hasError ? '1px' : base.borderWidth,
            boxShadow: hasError ? '0 0 0 1px red' : (state.isFocused ? base.boxShadow : 'none'),
            '&:hover': {
                borderColor: hasError ? 'red' : (state.isFocused ? '#2684ff' : '#b3b3b3'),
            },
        }),
        valueContainer: (base) => ({
            ...base,
            height: '24px',
            padding: '0 4px',
        }),
        input: (base) => ({
            ...base,
            margin: '0',
            padding: '0',
        }),
        indicatorsContainer: (base) => ({
            ...base,
            height: '24px',
        }),
        dropdownIndicator: (base) => ({
            ...base,
            padding: '0 4px',
        }),
        clearIndicator: (base) => ({
            ...base,
            padding: '0 4px',
        }),
        menuPortal: (base) => ({
            ...base,
            zIndex: 3000,
        }),
    });

    const selectPortalProps = {
        menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
        menuPosition: 'fixed',
    };

    const mergeSelectedOption = (options, selected) => {
        if (!selected) return options;
        if (options.some((option) => option.value === selected.value)) {
            return options;
        }
        return [selected, ...options];
    };

    const personSelectCommonProps = {
        ...selectPortalProps,
        filterOption: () => true,
        isSearchable: true,
        isClearable: true,
        noOptionsMessage: () => 'Няма опции',
        loadingMessage: () => 'Зареждане...',
    };
    const [editingId, setEditingId] = useState(null);
    const [editFromDate, setEditFromDate] = useState(null);
    const [editToDate, setEditToDate] = useState(null);
    const [editSelectedTitular, setEditSelectedTitular] = useState(null);
    const [editSelectedOccupant, setEditSelectedOccupant] = useState(null);
    const [editTitularMenuOpen, setEditTitularMenuOpen] = useState(false);
    const [editOccupantMenuOpen, setEditOccupantMenuOpen] = useState(false);

    // Helper function to format dates in Bulgarian format
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);

        if (!isNaN(date.getTime())) {
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            return `${day}.${month}.${year}`;
        }
        return dateString;
    };

    const isIndefiniteToDate = (dateValue) => {
        if (!dateValue) return false;

        if (dateValue instanceof Date) {
            return dateValue.getFullYear() === 2999
                && dateValue.getMonth() === 0
                && dateValue.getDate() === 1;
        }

        if (typeof dateValue === 'string') {
            if (dateValue.startsWith('2999-01-01') || dateValue === '01.01.2999') {
                return true;
            }

            return formatDate(dateValue) === '01.01.2999';
        }

        return false;
    };

    const formatToDateDisplay = (dateValue) => {
        if (isIndefiniteToDate(dateValue)) {
            return t('common.indefinite');
        }

        return formatDate(dateValue);
    };

    const getToDatePickerValue = (dateValue) => (isIndefiniteToDate(dateValue) ? '' : dateValue);

    const getToDatePlaceholder = (dateValue) => (
        isIndefiniteToDate(dateValue)
            ? t('common.indefinite')
            : t('organizationalStructure.structuralGroups.occupyingPossition.toDate')
    );

    const handleSave = async () => {
        try {
            setSaving(true);
            const submitData = {
                hrPositionId: editData.id,
                ocupantId: selectedOccupant.value,
                titularId: selectedTitular.value,
                fromDate: fromDate ? fromDate.toISOString().split('T')[0] : '',
                toDate: toDate ? toDate.toISOString().split('T')[0] : ''
            };

            await structuralGroupService.createPositionOccupationHistory(submitData);
            const response = await structuralGroupService.getPositionOccupationHistory(editData.id);
            setFilteredData(response);

            // Reset form
            setShowAddRow(false);
            setSelectedTitular(null);
            setSelectedOccupant(null);
            setFromDate('');
            setToDate('');
            setDocumentNumber('');
            reset();

            notifySuccess(t('common.createdSuccessfully'));
            if (onSuccess) {
                onSuccess();
            }
        } catch (error) {
            if (error.response?.status === 500) {
                const errorData = error.response?.data;
                const errorMessage = typeof errorData === 'string' ? errorData : (errorData?.message || '');
                
                    notifyError(errorMessage);
            } else {
                notifyError(t('common.errorMessage'));
            }
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = () => {
        if (selectedUsers.length === 0) return;

        const selectedItem = filteredData.find(item => item.id === selectedUsers[0]);
        if (selectedItem) {
            setEditingId(selectedItem.id);
            setEditFromDate(selectedItem.fromDate ? new Date(selectedItem.fromDate) : null);
            setEditToDate(selectedItem.toDate ? new Date(selectedItem.toDate) : null);

            // Set titular person
            if (selectedItem.titularId) {
                const titularOption = {
                    value: selectedItem.titularId.id,
                    label: `${selectedItem.titularId.name || ''} ${selectedItem.titularId.fathersName || ''} ${selectedItem.titularId.surname || ''}`.trim()
                };
                setEditSelectedTitular(titularOption);
            }

            // Set occupant person
            if (selectedItem.ocupantId) {
                const occupantOption = {
                    value: selectedItem.ocupantId.id,
                    label: `${selectedItem.ocupantId.name || ''} ${selectedItem.ocupantId.fathersName || ''} ${selectedItem.ocupantId.surname || ''}`.trim()
                };
                setEditSelectedOccupant(occupantOption);
            }

            setTitularPersons([]);
            setOccupantPersons([]);
        }
    };

    const handleUpdate = async () => {
        if (!editSelectedTitular || !editSelectedOccupant || !editFromDate) {
            return;
        }
        try {
            setSaving(true);
            const data = {
                hrPositionId: editData.id,
                ocupantId: editSelectedOccupant.value,
                titularId: editSelectedTitular.value,
                fromDate: editFromDate ? editFromDate.toISOString().split('T')[0] : '',
                toDate: editToDate ? editToDate.toISOString().split('T')[0] : ''
            };

            await structuralGroupService.updatePositionOccupationHistory(editingId, data);

            const response = await structuralGroupService.getPositionOccupationHistory(editData.id);
            setFilteredData(response);

            setEditingId(null);
            setEditSelectedTitular(null);
            setEditSelectedOccupant(null);
            setEditFromDate('');
            setEditToDate('');
            setEditTitularMenuOpen(false);
            setEditOccupantMenuOpen(false);
            setTitularPersons([]);
            setOccupantPersons([]);
            setSelectedUsers([]);

            notifySuccess(t('common.updatedSuccessfully'));
            if (onSuccess) {
                onSuccess();
            }
        } catch (error) {
            if (error.response?.status === 500) {
                const errorData = error.response?.data;
                const errorMessage = typeof errorData === 'string' ? errorData : (errorData?.message || '');
                
                    notifyError(errorMessage);
            } else {
                notifyError(t('common.errorMessage'));
            }
        } finally {
            setSaving(false);
        }
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditSelectedTitular(null);
        setEditSelectedOccupant(null);
        setEditFromDate('');
        setEditToDate('');
        setEditTitularMenuOpen(false);
        setEditOccupantMenuOpen(false);
        setTitularPersons([]);
        setOccupantPersons([]);
        setSelectedUsers([]);
    };


    const handleDelete = async (id) => {
        try {
            await structuralGroupService.deletePositionOccupationHistory(id);
            const response = await structuralGroupService.getPositionOccupationHistory(editData.id);
            setFilteredData(response);
            notifySuccess(t('common.deletedSuccessfully'));
            if (onSuccess) {
                onSuccess();
            }
        } catch (error) {
            notifyError(t('common.errorMessage'));
        }
    };

    const searchPersons = async (searchInput, setPersons, setIsLoading) => {
        if (!searchInput || searchInput.length < 2) {
            setPersons([]);
            return;
        }

        try {
            setIsLoading(true);

            const searchTerms = searchInput.trim().split(/\s+/);
            let searchParams = {};

            if (searchTerms.length === 1) {
                searchParams.name = searchTerms[0];
            } else if (searchTerms.length === 2) {
                searchParams.name = searchTerms[0];
                searchParams.fathersName = searchTerms[1];
            } else if (searchTerms.length >= 3) {
                searchParams.name = searchTerms[0];
                searchParams.fathersName = searchTerms[1];
                searchParams.surname = searchTerms[2];
            }

            const response = await signeesService.searchPersons(searchParams);
            const personOptions = (response.content || []).map(person => ({
                value: person.id,
                label: `${person.name || ''} ${person.fathersName || ''} ${person.surname || ''}`.trim()
            }));

            setPersons(personOptions);
        } catch (error) {
            console.error('Error searching persons:', error);
            setPersons([]);
        } finally {
            setIsLoading(false);
        }
    };

    const debouncedSearchTitular = useDebounce((input) => searchPersons(input, setTitularPersons, setIsLoadingTitular), 1000);
    const debouncedSearchOccupant = useDebounce((input) => searchPersons(input, setOccupantPersons, setIsLoadingOccupant), 1000);

    const handleTitularSelection = (selectedOption) => {
        setSelectedTitular(selectedOption);
        setValue('titular', selectedOption?.value || '', { shouldValidate: true });
    };

    const handleOccupantSelection = (selectedOption) => {
        setSelectedOccupant(selectedOption);
        setValue('occupant', selectedOption?.value || '', { shouldValidate: true });
    };

    const handleFromDateChange = (date) => {
        setFromDate(date);
        setValue('fromDate', date, { shouldValidate: true });
    };

    useEffect(() => {
        const fetchOccupationHistory = async () => {
            if (!editData?.id) return;

            try {
                setLoading(true);
                setError(null);
                const response = await structuralGroupService.getPositionOccupationHistory(editData.id);
                setFilteredData(response);
            } catch (err) {
                console.error('Error fetching occupation history:', err);
                setError('Failed to load occupation history');
            } finally {
                setLoading(false);
            }
        };

        fetchOccupationHistory();
    }, [editData]);
    useEffect(() => {
        setLoading(true);
        setError(null);
        setFilteredData([]);
        setSelectedUsers([]);
    }, [editData]);


    const handleCheckboxChange = (userId) => {
        setSelectedUsers(prev =>
            prev.includes(userId)
                ? []
                : [userId]
        );
    };

    const handleAddClick = () => {
        setShowAddRow(true);
    };


    const actionButtons = (
        <>
            <button
                className="icon-button"
                onClick={handleAddClick}
                title={t('common.add')}
            >
                <FontAwesomeIcon icon={faPlus} />
            </button>
            <button
                className={`icon-button ${!selectedUsers.length ? 'disabled-link' : ''}`}
                disabled={!selectedUsers.length}
                onClick={handleEdit}
                title={t('common.edit')}
            >
                <FontAwesomeIcon icon={faEdit} />
            </button>
            <button
                className={`icon-button ${!selectedUsers.length ? 'disabled-link' : ''}`}
                disabled={!selectedUsers.length}
                onClick={() => handleDelete(selectedUsers[0])}
                title={t('common.delete')}
            >
                <FontAwesomeIcon icon={faTrash} />
            </button>
        </>
    )

    return (
        <div className="modal show" tabIndex="-1">
            <div ref={modalRef} className="form-wrapper top-20 width-80">
                <div className="form">
                    <h2 className="title">{t('organizationalStructure.structuralGroups.occupyingPossition.title')}</h2>
                    <div className="flex-row justify-content-center">
                        {actionButtons}
                    </div>
                    <div className="table-responsive custom-table-container">
                        <table className="table table-hover">
                            <thead>
                                <tr>
                                    <th className="checkbox-column">
                                        <div className="checkbox-wrapper">
                                        </div>
                                    </th>
                                    <th>{t('organizationalStructure.structuralGroups.occupyingPossition.fromDate')}</th>
                                    <th>{t('organizationalStructure.structuralGroups.occupyingPossition.toDate')}</th>
                                    <th>{t('organizationalStructure.structuralGroups.occupyingPossition.titular')}</th>
                                    <th>{t('organizationalStructure.structuralGroups.occupyingPossition.occupant')}</th>
                                    <th>{t('organizationalStructure.structuralGroups.occupyingPossition.documentNumber')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {showAddRow && (
                                    <tr className="add-row">
                                        <td className="checkbox-column">
                                            <div className="checkbox-wrapper">
                                            </div>
                                        </td>
                                        <td>
                                            <CustomDatePicker
                                                name="fromDate"
                                                value={fromDate}
                                                onChange={handleFromDateChange}
                                                isSmall={true}
                                                placeholderText={errors.fromDate ? errors.fromDate.message : t('common.required')}
                                                className={errors.fromDate ? 'input-error' : ''}
                                                renderInPortal={true}
                                                popperPlacement="top-start"
                                            />
                                        </td>
                                        <td>
                                            <CustomDatePicker
                                                name="toDate"
                                                value={getToDatePickerValue(toDate)}
                                                onChange={setToDate}
                                                isSmall={true}
                                                placeholderText={getToDatePlaceholder(toDate)}
                                                renderInPortal={true}
                                                popperPlacement="top-start"
                                            />
                                        </td>
                                        <td>
                                            <Select
                                                {...personSelectCommonProps}
                                                styles={customSelectStyles(!!errors.titular)}
                                                placeholder={errors.titular ? errors.titular.message : t('common.required')}
                                                options={mergeSelectedOption(titularPersons, selectedTitular)}
                                                value={selectedTitular}
                                                onChange={handleTitularSelection}
                                                onInputChange={(inputValue) => {
                                                    if (inputValue && inputValue.length >= 2) {
                                                        debouncedSearchTitular(inputValue);
                                                    } else {
                                                        setTitularPersons([]);
                                                    }
                                                    return inputValue;
                                                }}
                                                isLoading={isLoadingTitular}
                                            />
                                        </td>
                                        <td>
                                            <Select
                                                {...personSelectCommonProps}
                                                styles={customSelectStyles(!!errors.occupant)}
                                                placeholder={errors.occupant ? errors.occupant.message : t('common.required')}
                                                options={mergeSelectedOption(occupantPersons, selectedOccupant)}
                                                value={selectedOccupant}
                                                onChange={handleOccupantSelection}
                                                onInputChange={(inputValue) => {
                                                    if (inputValue && inputValue.length >= 2) {
                                                        debouncedSearchOccupant(inputValue);
                                                    } else {
                                                        setOccupantPersons([]);
                                                    }
                                                    return inputValue;
                                                }}
                                                isLoading={isLoadingOccupant}
                                            />
                                        </td>
                                        <td>
                                            <input type="text" className="small-input" placeholder={t('organizationalStructure.structuralGroups.occupyingPossition.documentNumber')} />
                                        </td>
                                    </tr>
                                )}
                                {loading ? (
                                    <tr>
                                        <td colSpan="4" className="text-center">Loading...</td>
                                    </tr>
                                ) : error ? (
                                    <tr>
                                        <td colSpan="4" className="text-center text-danger">{error}</td>
                                    </tr>
                                ) : filteredData.length === 0 ? (
                                    <tr>
                                        <td colSpan="4" className="text-center">No users available to add</td>
                                    </tr>
                                ) : (
                                    filteredData.map((item) => (
                                        <tr key={item.id}>
                                            <td className="checkbox-column" onClick={(e) => e.stopPropagation()}>
                                                <div className="checkbox-wrapper">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedUsers.includes(item.id)}
                                                        onChange={() => handleCheckboxChange(item.id)}
                                                    />
                                                </div>
                                            </td>
                                            <td>
                                                {editingId === item.id ? (
                                                    <CustomDatePicker
                                                        name="editFromDate"
                                                        value={editFromDate}
                                                        onChange={setEditFromDate}
                                                        isSmall={true}
                                                        placeholderText={t('organizationalStructure.structuralGroups.occupyingPossition.fromDate')}
                                                        renderInPortal={true}
                                                        popperPlacement="top-start"
                                                    />
                                                ) : (
                                                    formatDate(item.fromDate)
                                                )}
                                            </td>
                                            <td>
                                                {editingId === item.id ? (
                                                    <CustomDatePicker
                                                        name="editToDate"
                                                        value={getToDatePickerValue(editToDate)}
                                                        onChange={setEditToDate}
                                                        isSmall={true}
                                                        placeholderText={getToDatePlaceholder(editToDate)}
                                                        renderInPortal={true}
                                                        popperPlacement="top-start"
                                                    />
                                                ) : (
                                                    formatToDateDisplay(item.toDate)
                                                )}
                                            </td>
                                            <td>
                                                {editingId === item.id ? (
                                                    <Select
                                                        {...personSelectCommonProps}
                                                        styles={customSelectStyles(false)}
                                                        placeholder="Име на титуляра"
                                                        options={titularPersons}
                                                        value={editSelectedTitular}
                                                        onChange={setEditSelectedTitular}
                                                        controlShouldRenderValue={!editTitularMenuOpen}
                                                        hideSelectedOptions={true}
                                                        onMenuOpen={() => {
                                                            setEditTitularMenuOpen(true);
                                                            setTitularPersons([]);
                                                        }}
                                                        onMenuClose={() => setEditTitularMenuOpen(false)}
                                                        onInputChange={(inputValue) => {
                                                            if (inputValue && inputValue.length >= 2) {
                                                                debouncedSearchTitular(inputValue);
                                                            } else {
                                                                setTitularPersons([]);
                                                            }
                                                            return inputValue;
                                                        }}
                                                        isLoading={isLoadingTitular}
                                                    />
                                                ) : (
                                                    `${item.titularId?.name || ''} ${item.titularId?.fathersName || ''} ${item.titularId?.surname || ''}`
                                                )}
                                            </td>
                                            <td>
                                                {editingId === item.id ? (
                                                    <Select
                                                        {...personSelectCommonProps}
                                                        styles={customSelectStyles(false)}
                                                        placeholder="Име на заемащия"
                                                        options={occupantPersons}
                                                        value={editSelectedOccupant}
                                                        onChange={setEditSelectedOccupant}
                                                        controlShouldRenderValue={!editOccupantMenuOpen}
                                                        hideSelectedOptions={true}
                                                        onMenuOpen={() => {
                                                            setEditOccupantMenuOpen(true);
                                                            setOccupantPersons([]);
                                                        }}
                                                        onMenuClose={() => setEditOccupantMenuOpen(false)}
                                                        onInputChange={(inputValue) => {
                                                            if (inputValue && inputValue.length >= 2) {
                                                                debouncedSearchOccupant(inputValue);
                                                            } else {
                                                                setOccupantPersons([]);
                                                            }
                                                            return inputValue;
                                                        }}
                                                        isLoading={isLoadingOccupant}
                                                    />
                                                ) : (
                                                    `${item.ocupantId?.name || ''} ${item.ocupantId?.fathersName || ''} ${item.ocupantId?.surname || ''}`
                                                )}
                                            </td>
                                            <td>{item.documentNumber || ''}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>

                    </div>
                    <div className="form-controls-container">
                        {editingId ? (
                            <>
                                <LoadingButton
                                    className="form-btn btn-blue"
                                    onClick={handleUpdate}
                                    loading={saving}
                                    disabled={!editSelectedTitular || !editSelectedOccupant || !editFromDate}
                                >
                                    {t('common.update')}
                                </LoadingButton>
                                <button
                                    type="button"
                                    className="form-btn btn-red"
                                    onClick={handleCancelEdit}
                                >
                                    {t('common.cancel')}
                                </button>
                            </>
                        ) : (
                            <>
                            <LoadingButton
                                className="form-btn btn-blue"
                                onClick={handleSubmit(handleSave)}
                                loading={saving}
                                disabled={!showAddRow}
                            >
                                {t('common.save')}
                            </LoadingButton>
                            <button
                                type="button"
                                className="form-btn btn-red"
                                onClick={() => {
                                    if (showAddRow) {
                                        setShowAddRow(false);
                                        setSelectedTitular(null);
                                        setSelectedOccupant(null);
                                        setFromDate('');
                                        setToDate('');
                                        setDocumentNumber('');
                                        reset();
                                    } else {
                                        onClose(false);
                                    }
                                }}
                            >
                                {t('common.cancel')}
                            </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div >
    )
}